import React, { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { FaInstagram, FaFacebookF, FaTiktok, FaYoutube } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './gallery.css';

const imageList = [
  "/images/ZEBRA CROSSING.jpg",
  "/images/lionessmoment.jpg",
  "/images/wildbeast.jpg",
  "/images/hyena.jpg",
  "/images/NIGHTDRIVE.JPG",
  "/images/elephant.jpg",
  "/images/antelope.jpg",
  "/images/leopardhunt.jpg",
  "/images/bird.jpg",
  "/images/cheetah.jpg",
  "/images/LIONKINGS.jpg",
  "/images/ffleetmuddy.jpeg",
  "/images/lioness.jpg",
  "/images/elephant1.jpg",
  "/images/cheetah12.jpg",
  "/images/cub.jpg",
  "/images/leopard3.jpg",
  "/images/lionfam.jpg",
  "/images/hunt2.jpg",
  "/images/LIONESCUBS.jpg",
  "/images/birdflying.jpg",
  "/images/elephants.jpg",
  "/images/family.JPG",
  "/images/DOMINATE.JPG",
];

const altTexts = [
  'Photo of zebra crossing in the wild',
  'Photo of a lioness in a dramatic moment',
  'Photo of wildebeest in motion',
  'Photo of a hyena in its habitat',
  'Photo of a night drive safari experience',
  'Photo of an elephant in the savanna',
  'Photo of an antelope standing alert',
  'Photo of a leopard during a hunt',
  'Photo of a bird perched or flying',
  'Photo of a cheetah scanning the horizon',
  'Photo of a pride of lions titled "Lion Kings"',
  'Photo of safari fleet in muddy terrain',
  'Photo of a resting lioness',
  'Photo of an elephant with tusks visible',
  'Photo of a cheetah captured mid-stride',
  'Photo of a lion cub in the grasslands',
  'Photo of a leopard resting on a branch',
  'Photo of a lion family together',
  'Photo of a hunting scene in the wild',
  'Photo of lioness with cubs',
  'Photo of a bird in mid-flight',
  'Photo of a group of elephants walking',
  'Photo of a family enjoying a safari',
  'Photo titled "Dominate" showing wild dominance'
];

const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const [page, setPage] = useState(0);
  const imagesPerPage = 8;
  const totalPages = Math.ceil(imageList.length / imagesPerPage);

  const startIndex = page * imagesPerPage;
  const visibleImages = imageList.slice(startIndex, startIndex + imagesPerPage);

  const { t } = useTranslation('flysafari');

  return (
    <div>
      {/* Hero Section */}
      <section className="gallery-hero">
        <img 
          src="/images/marariver.jpg"
          alt="Olenini Gallery" 
          className="galhero-image" 
          loading="eager" 
        />
      </section>

      <h1 className="gallery-title">Explore Our Gallery</h1>

      {/* Safari Photo Grid */}
      <div className="gallery-grid">
        {visibleImages.map((imgSrc, index) => (
          <div className="gallery-item" key={startIndex + index}>
            <img
              src={imgSrc}
              alt={altTexts[startIndex + index]}
              className="clickable-image"
              loading="lazy"
              onClick={() => {
                setPhotoIndex(startIndex + index);
                setLightboxOpen(true);
              }}
            />
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button 
          onClick={() => setPage(page - 1)} 
          disabled={page === 0} 
          className="page-btn"
        >
          ◀ Prev
        </button>
        <span>
          Page {page + 1} of {totalPages}
        </span>
        <button 
          onClick={() => setPage(page + 1)} 
          disabled={page === totalPages - 1} 
          className="page-btn"
        >
          Next ▶
        </button>
      </div>

      {/* Foundation (Medical) Gallery */}
      <div className="gallery-category">
        <h4>{t('gallery.foundation_title')}</h4>
        <p>{t('gallery.description')}</p>
        <Slider
          autoplay={true}
          autoplaySpeed={2500}
          infinite={true}
          slidesToShow={3}
          slidesToScroll={1}
          arrows={true}
          dots={false}
          pauseOnHover={true}
          swipeToSlide={true}
          responsive={[
            { breakpoint: 1024, settings: { slidesToShow: 2 } },
            { breakpoint: 600, settings: { slidesToShow: 1 } },
          ]}
        >
          <div><img src="/images/lab.jpg" alt="Medical Outreach 1" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/medic.jpg" alt="Medical Outreach 2" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/mentorship.jpg" alt="Medical Outreach 3" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/dentist.jpg" alt="Medical Outreach 4" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/volunteerservice.jpg" alt="Medical Outreach 5" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/img_6942.jpg" alt="Medical Outreach 6" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/img_6845.jpg" alt="Medical Outreach 7" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/clinic.jpeg" alt="Medical Outreach 8" loading='lazy' className="gallery-image" /></div>
          <div><img src="/images/treat.jpeg" alt="Medical Outreach 9" loading='lazy' className="gallery-image" /></div>
        </Slider>
      </div>

      <hr />

      {/* CTA + Social Links */}
      <section className="gallery-cta luxury-card">
        <h2>{t('galleryCta.title')}</h2>
        <p className="intro-text">{t('galleryCta.description')}</p>
        <a href="/contact" className="cta-button">{t('galleryCta.button')}</a>

        <div className="social-links">
          <p className="follow-us">{t('galleryCta.follow')}</p>
          <div className="social-icons">
            <a href="https://www.instagram.com/olenininisafaris" target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
            <a href="https://www.facebook.com/oleninisafaris" target="_blank" rel="noopener noreferrer"><FaFacebookF /></a>
            <a href="https://www.tiktok.com/@oleninisafaris" target="_blank" rel="noopener noreferrer"><FaTiktok /></a>
            <a href="https://x.com/olenini_safaris" target="_blank" rel="noopener noreferrer"><FaXTwitter /></a>
            <a href="https://www.youtube.com/@Oleninisafaris-tours" target="_blank" rel="noopener noreferrer"><FaYoutube /></a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={photoIndex}
        slides={imageList.map((src) => ({ src }))}
        on={{ view: ({ index }) => setPhotoIndex(index) }}
      />
    </div>
  );
};

export default Gallery;
